import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn, Modal, Field, Input, Select, StatusPill } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";
import { Plus, Trash2, Edit3, UserCircle } from "lucide-react";

const empty = {
  name: "", mobile: "", aadhaar_number: "", pan: "",
  license_number: "", license_expiry: "", badge_number: "", badge_expiry: "",
  status: "active",
};

export default function Drivers() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/owner/drivers")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const d = { ...editing };
    ["license_expiry","badge_expiry"].forEach(k => { if (!d[k]) d[k] = null; });
    if (d.id) await api.patch(`/owner/drivers/${d.id}`, d);
    else await api.post("/owner/drivers", d);
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => {
    if (!confirm("Delete driver?")) return;
    await api.delete(`/owner/drivers/${id}`); load();
  };

  return (
    <div data-testid="drivers-page">
      <PageHeader title="Drivers" subtitle="License, badge & verification on a single sheet."
        actions={<Btn data-testid="driver-add" onClick={() => { setEditing({...empty}); setOpen(true); }}><Plus className="h-4 w-4" /> Add Driver</Btn>}
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Name</th><th>Mobile</th><th>License</th><th>Lic. Expiry</th><th>Badge</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500"><UserCircle className="h-8 w-8 mx-auto mb-2 text-zinc-300" /> No drivers yet.</td></tr>
            ) : items.map((d) => (
              <tr key={d.id}>
                <td className="font-medium text-asphalt">{d.name}</td>
                <td>{d.mobile}</td>
                <td className="font-mono text-xs">{d.license_number}</td>
                <td>{formatDate(d.license_expiry)}</td>
                <td>{d.badge_number || "—"}</td>
                <td><StatusPill status={d.status} /></td>
                <td className="text-right">
                  <Btn variant="ghost" onClick={() => { setEditing(d); setOpen(true); }} data-testid={`driver-edit-${d.id}`}><Edit3 className="h-4 w-4" /></Btn>
                  <Btn variant="ghost" onClick={() => del(d.id)} data-testid={`driver-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} size="lg" onClose={() => setOpen(false)} title={editing?.id ? "Edit Driver" : "Add Driver"} testid="driver-modal"
        footer={<>
          <Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn onClick={submit} data-testid="driver-save">Save</Btn>
        </>}>
        {editing && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Name" required><Input data-testid="drv-name" value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} required /></Field>
            <Field label="Mobile" required><Input data-testid="drv-mobile" value={editing.mobile} onChange={(e) => setEditing({...editing, mobile: e.target.value})} required /></Field>
            <Field label="License Number" required><Input data-testid="drv-license" value={editing.license_number} onChange={(e) => setEditing({...editing, license_number: e.target.value})} required /></Field>
            <Field label="License Expiry"><Input type="date" value={editing.license_expiry || ""} onChange={(e) => setEditing({...editing, license_expiry: e.target.value})} /></Field>
            <Field label="Aadhaar"><Input value={editing.aadhaar_number || ""} onChange={(e) => setEditing({...editing, aadhaar_number: e.target.value})} /></Field>
            <Field label="PAN"><Input value={editing.pan || ""} onChange={(e) => setEditing({...editing, pan: e.target.value})} /></Field>
            <Field label="Badge Number"><Input value={editing.badge_number || ""} onChange={(e) => setEditing({...editing, badge_number: e.target.value})} /></Field>
            <Field label="Badge Expiry"><Input type="date" value={editing.badge_expiry || ""} onChange={(e) => setEditing({...editing, badge_expiry: e.target.value})} /></Field>
            <Field label="Status">
              <Select value={editing.status} onChange={(e) => setEditing({...editing, status: e.target.value})}>
                <option value="active">Active</option><option value="on_trip">On Trip</option><option value="leave">Leave</option><option value="suspended">Suspended</option>
              </Select>
            </Field>
          </form>
        )}
      </Modal>
    </div>
  );
}
